import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, ShieldCheck, Lock, KeyRound, ServerCog, Fingerprint,
  Phone, CreditCard, Users, MapPin, Trash2, Download, RefreshCw, Eye,
  Mail, ArrowRight, FileCheck2, Sparkles, BookOpen, MessageCircle, ScrollText,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useRGPDGamification, BADGES } from '../hooks/useRGPDGamification';
import { SecurityLeaderboard } from '../components/SecurityLeaderboard';

const iconFor = (name) => ({
  BookOpen, MessageCircle, ScrollText, FileCheck2,
}[name] || Sparkles);

function SectionHead({ eyebrow, title, kicker }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-[#5a8268]">{eyebrow}</div>
      <h2 className="mt-2 text-[clamp(1.6rem,3vw,2.4rem)] leading-[1.08] font-semibold tracking-tight text-[#1a1d1a]">{title}</h2>
      {kicker && <p className="mt-3 text-[14.5px] text-neutral-600 max-w-2xl leading-relaxed">{kicker}</p>}
    </div>
  );
}

function DataCard({ Icon, title, body, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 card-lift">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3.5 text-[14.5px] font-semibold text-[#1a1d1a]">{title}</div>
      <p className="mt-1 text-[13px] text-neutral-600 leading-relaxed">{body}</p>
    </div>
  );
}

function RightRow({ Icon, title, body }) {
  return (
    <div className="flex gap-3.5 rounded-2xl bg-white border border-black/5 p-4">
      <div className="w-10 h-10 rounded-xl bg-[#f5f4ef] flex items-center justify-center shrink-0 text-[#1a1d1a]">
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[14px] font-semibold text-[#1a1d1a]">{title}</div>
        <p className="mt-0.5 text-[12.5px] text-neutral-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

export default function RGPDPage() {
  const { state, unlock, progress, isMaster, MASTER_THRESHOLD } = useRGPDGamification();
  const [name, setName] = useState('');
  const containerRef = useRef(null);

  // Award "video-watched" style badge for time-on-page
  useEffect(() => {
    const t = setTimeout(() => unlock('video-watched'), 30_000);
    return () => clearTimeout(t);
  }, [unlock]);

  // Award "charter-read" when user scrolls past 60% of the page
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / doc.scrollHeight;
      if (scrolled >= 0.6) unlock('charter-read');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [unlock]);

  const downloadCertificate = () => {
    const holder = (name || 'Bénéficiaire Fodindin').trim();
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const certId = `RGPD-${now.getFullYear()}-FD-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const w = pdf.internal.pageSize.getWidth();
    const h = pdf.internal.pageSize.getHeight();

    // Cream background
    pdf.setFillColor(245, 244, 239);
    pdf.rect(0, 0, w, h, 'F');

    // Header stripe
    pdf.setFillColor(26, 29, 26);
    pdf.rect(0, 0, w, 32, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text('FODINDIN · SOLIDARITÉ', 20, 14);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Attestation de conformité RGPD', 20, 24);

    // Body card
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(18, 46, w - 36, h - 76, 6, 6, 'F');

    pdf.setTextColor(90, 130, 104);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CERTIFICAT NOMINATIF', 28, 60);

    pdf.setTextColor(26, 29, 26);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Vos données sont protégées.', 28, 74);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(70, 70, 70);
    const intro = pdf.splitTextToSize(
      "Fodindin atteste que les données personnelles et financières de son utilisateur nommé ci-dessous sont traitées selon le RGPD (UE 2016/679), la Loi ivoirienne 2016-13 et les directives UEMOA.",
      w - 60,
    );
    pdf.text(intro, 28, 84);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(120, 120, 120);
    pdf.text('BÉNÉFICIAIRE', 28, 110);
    pdf.setFontSize(18);
    pdf.setTextColor(26, 29, 26);
    pdf.text(holder, 28, 120);

    // Two-column details
    const rows = [
      ['Numéro de certificat', certId],
      ['Date de délivrance', dateStr],
      ['Niveau de chiffrement', 'AES-256 · TLS 1.3'],
      ['Authentification', 'OTP SMS + JWT signé HS256'],
      ['Hébergement', 'MongoDB Atlas · CI + UE (chiffré au repos)'],
      ['Délégué à la protection', 'dpo@fodindincagnotte.com'],
    ];
    let y = 138;
    rows.forEach(([k, v]) => {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(140, 140, 140);
      pdf.text(k.toUpperCase(), 28, y);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(26, 29, 26);
      pdf.text(v, 28, y + 5.5);
      y += 14;
    });

    // Right-side seal
    pdf.setFillColor(217, 119, 87);
    pdf.circle(w - 42, 128, 18, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RGPD', w - 42, 126, { align: 'center' });
    pdf.text('COMPLIANT', w - 42, 132, { align: 'center' });

    // Footer note
    pdf.setTextColor(120, 120, 120);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      "Ce certificat est délivré à titre informatif. Il peut être vérifié auprès du DPO Fodindin (dpo@fodindincagnotte.com).",
      28,
      h - 38,
      { maxWidth: w - 56 },
    );
    pdf.setTextColor(26, 29, 26);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('fodindincagnotte.com', 28, h - 26);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Émis le ${dateStr} · ID ${certId}`, w - 28, h - 26, { align: 'right' });

    pdf.save(`attestation-rgpd-fodindin-${certId}.pdf`);
    unlock('certificate-downloaded');
  };

  return (
    <div ref={containerRef}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #f5f4ef 0%, #fff 100%)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-14">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-medium text-[#5a8268]">
                <Shield className="w-3.5 h-3.5" /> Charte RGPD
              </div>
              <h1 className="mt-3 text-[clamp(2.4rem,5.4vw,4.4rem)] leading-[1.02] font-semibold tracking-tight text-[#1a1d1a]">
                Vos données, <span className="serif-italic text-[#d97757]">gardées</span> comme les nôtres.
              </h1>
              <p className="mt-5 text-[15.5px] text-neutral-700 max-w-xl leading-relaxed">
                Fodindin est conforme au <strong>RGPD</strong> (UE 2016/679), à la <strong>Loi ivoirienne 2016‑13</strong> sur la protection des données personnelles et aux <strong>directives UEMOA</strong>. Cette page détaille exactement ce que nous collectons, comment nous le protégeons, et vos droits.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-[12px] text-neutral-700">
                {['AES‑256', 'TLS 1.3', 'JWT signé', 'bcrypt', 'Zéro cookie publicitaire'].map((chip) => (
                  <span key={chip} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/5 px-3 py-1">
                    <ShieldCheck className="w-3 h-3 text-[#5a8268]" /> {chip}
                  </span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#attestation" className="btn-anim inline-flex items-center gap-2 rounded-full bg-[#1a1d1a] hover:bg-black text-white h-11 px-5 text-[13.5px] font-medium" data-testid="rgpd-hero-download-cta">
                  <Download className="w-4 h-4" /> Obtenir mon attestation
                </a>
                <a href="#droits" className="btn-anim inline-flex items-center gap-2 rounded-full bg-white border border-black/10 hover:border-black/20 text-[#1a1d1a] h-11 px-5 text-[13.5px]">
                  Voir mes droits <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Gamification widget */}
            <div className="rounded-3xl bg-[#1a1d1a] text-white p-6 sm:p-7 border border-white/10 shadow-xl">
              <div className="flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-white/60">
                <Sparkles className="w-3.5 h-3.5 text-[#d97757]" /> Votre progression
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="text-[42px] leading-none font-semibold" data-testid="rgpd-points-value">{state.points}</div>
                <div className="text-[13px] text-white/50">/ {MASTER_THRESHOLD} points</div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, background: isMaster ? '#5a8268' : '#d97757' }} />
              </div>
              <div className="mt-2 text-[11.5px] text-white/60">
                {isMaster ? 'Bravo, vous êtes Maître RGPD ✨' : `Plus que ${MASTER_THRESHOLD - state.points} pts pour devenir Maître RGPD`}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2" data-testid="rgpd-badges">
                {BADGES.map((b) => {
                  const Ico = iconFor(b.icon);
                  const unlocked = !!state.unlocked[b.id];
                  return (
                    <div
                      key={b.id}
                      data-testid={`rgpd-badge-${b.id}`}
                      className={`rounded-xl p-3 border transition-all ${unlocked ? 'bg-[#d97757]/15 border-[#d97757]/40' : 'bg-white/5 border-white/10'}`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${unlocked ? 'bg-[#d97757] text-white' : 'bg-white/10 text-white/40'}`}>
                        <Ico className="w-3.5 h-3.5" />
                      </div>
                      <div className="mt-2 text-[11.5px] font-semibold">{b.label}</div>
                      <div className={`mt-0.5 text-[10.5px] ${unlocked ? 'text-[#d97757]' : 'text-white/40'}`}>
                        {unlocked ? `+${b.points} pts` : b.hint}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Data we collect */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Section 1"
            title={<>Ce que nous <span className="serif-italic text-[#d97757]">collectons</span>, rien de plus.</>}
            kicker="Le minimum nécessaire pour faire fonctionner votre cagnotte. Nous n'utilisons ni cookie publicitaire, ni pixel de tracking tiers."
          />
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <DataCard Icon={Phone} accent="#d97757" title="Identité de connexion" body="Numéro de téléphone (pour l'OTP), prénom optionnel. Aucun email obligatoire." />
            <DataCard Icon={CreditCard} accent="#5a8268" title="Paiements" body="Uniquement une référence de transaction Paystack/CinetPay. Nous ne stockons ni votre PAN, ni votre code Mobile Money." />
            <DataCard Icon={Users} accent="#0e3a3a" title="Contacts d'invitation" body="Numéros que vous saisissez pour inviter — jamais importés automatiquement, jamais partagés à des tiers." />
            <DataCard Icon={MapPin} accent="#d97757" title="Pays" body="Pays de la cagnotte (choisi par vous). Aucune géolocalisation GPS n'est demandée." />
            <DataCard Icon={Fingerprint} accent="#5a8268" title="Biométrie (optionnelle)" body="Si vous activez WebAuthn, la clé publique reste sur votre appareil. Fodindin ne voit jamais l'empreinte." />
            <DataCard Icon={ServerCog} accent="#1a1d1a" title="Journaux techniques" body="Adresse IP + horodatage des connexions, purgés après 12 mois. Utilisés uniquement pour détecter la fraude." />
          </div>
        </div>
      </section>

      {/* Section 2: Security */}
      <section className="py-16 sm:py-20 bg-[#f5f4ef]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Section 2"
            title={<>Notre <span className="serif-italic text-[#5a8268]">sécurité</span>, au niveau des banques.</>}
            kicker="Les mêmes standards que ceux imposés par les régulateurs bancaires africains — appliqués à une plateforme légère."
          />
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <RightRow Icon={Lock} title="Chiffrement AES‑256 & TLS 1.3" body="Toutes les données sensibles sont chiffrées au repos (MongoDB Atlas) et en transit. Impossible d'écouter la connexion, impossible de lire un backup." />
            <RightRow Icon={KeyRound} title="Mots de passe : bcrypt + JWT signé" body="Les mots de passe admin sont hachés (bcrypt). Les tokens de session sont signés (HS256) et expirent automatiquement." />
            <RightRow Icon={ShieldCheck} title="OTP à usage unique" body="Chaque connexion nécessite un code à 6 chiffres envoyé par SMS, valable 10 minutes. 5 échecs = blocage." />
            <RightRow Icon={ServerCog} title="Hébergement redondé" body="Serveurs UE + réplication CI. Sauvegardes chiffrées quotidiennes. Aucun accès humain aux données sans traçabilité." />
            <RightRow Icon={Eye} title="Détection d'anomalies" body="Chaque don anormal (montant, opérateur, IP) déclenche une alerte au DPO. Compte gelable en 2h en cas de doute." />
            <RightRow Icon={Shield} title="Aucun partage marchand" body="Vos données ne sont jamais vendues ni louées. Zéro publicité comportementale, zéro Facebook Pixel, zéro Google Ads." />
          </div>
        </div>
      </section>

      {/* Security leaderboard */}
      <SecurityLeaderboard />

      {/* Section 3: Your rights */}
      <section id="droits" className="py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Section 3"
            title={<>Vos <span className="serif-italic text-[#d97757]">droits</span>, sans jargon.</>}
            kicker="Article 12 à 22 du RGPD, résumés en français clair. Écrivez à dpo@fodindincagnotte.com — réponse garantie sous 48 h ouvrées."
          />
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <RightRow Icon={Eye} title="Droit d'accès" body="Demandez à voir toutes les données que nous détenons sur vous. Nous envoyons un export lisible en 48 h max." />
            <RightRow Icon={RefreshCw} title="Droit de rectification" body="Une info fausse ? On corrige immédiatement, depuis votre tableau de bord ou sur simple email." />
            <RightRow Icon={Trash2} title="Droit à l'effacement" body="Supprimez votre compte à tout moment. Données personnelles effacées en 30 jours ; historiques financiers archivés 5 ans (obligation fiscale)." />
            <RightRow Icon={Download} title="Droit à la portabilité" body="Récupérez toutes vos cagnottes et dons au format JSON pour les migrer où vous voulez." />
            <RightRow Icon={ShieldCheck} title="Droit d'opposition" body="Refusez que vos données servent à des statistiques d'usage. Zéro impact sur le fonctionnement de vos cagnottes." />
            <RightRow Icon={Mail} title="Droit de plainte" body="Une décision vous semble injuste ? Saisissez l'ARTCI (Côte d'Ivoire) ou la CNIL (UE) — nous coopérons sans réserve." />
          </div>
          <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#fff3ec] via-[#fdebd9] to-[#eaf1e7] border border-black/5 p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-[#5a8268]">Contact DPO</div>
                <h3 className="mt-1.5 text-[22px] font-semibold text-[#1a1d1a]">Une question sur vos données ?</h3>
                <p className="mt-1 text-[14px] text-neutral-700">Un humain répond sous 48 h ouvrées, en français.</p>
              </div>
              <a
                href="mailto:dpo@fodindincagnotte.com"
                className="btn-anim inline-flex items-center gap-2 rounded-full bg-[#1a1d1a] hover:bg-black text-white h-11 px-5 text-[13.5px] font-medium"
              >
                <Mail className="w-4 h-4" /> dpo@fodindincagnotte.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Certificate */}
      <section id="attestation" className="py-16 sm:py-20 bg-[#1a1d1a] text-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-[#d97757]">Attestation nominative</div>
            <h2 className="mt-2 text-[clamp(1.8rem,3.4vw,2.8rem)] leading-[1.05] font-semibold tracking-tight">
              Téléchargez votre <span className="serif-italic text-[#d97757]">certificat</span> RGPD.
            </h2>
            <p className="mt-4 text-[15px] text-white/70 leading-relaxed max-w-xl">
              Génère instantanément un PDF signé numériquement avec votre nom, la date et un identifiant unique. Utile pour prouver la conformité auprès d&apos;un partenaire, d&apos;une banque ou d&apos;une association bénéficiaire.
            </p>
            <div className="mt-6 space-y-3 max-w-md">
              <label className="text-[12px] font-medium text-white/70 block">Nom à faire figurer sur le certificat</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex. Awa Koné, Association Ivoire Solidaire…"
                data-testid="rgpd-certificate-name-input"
                className="h-11 rounded-full bg-white/5 border-white/15 text-white placeholder-white/40 px-4"
              />
              <Button
                onClick={downloadCertificate}
                data-testid="rgpd-download-certificate-btn"
                className="btn-anim h-11 rounded-full bg-[#d97757] hover:bg-[#c66745] text-white text-[13.5px] font-medium px-5"
              >
                <FileCheck2 className="w-4 h-4 mr-2" /> Générer & télécharger le PDF
              </Button>
            </div>
          </div>
          <div className="rounded-3xl bg-white text-[#1a1d1a] p-8 shadow-2xl border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5a8268]">Certificat nominatif</div>
                <div className="mt-1.5 text-[11px] text-neutral-500">FODINDIN · SOLIDARITÉ</div>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#d97757] text-white flex items-center justify-center text-[10px] font-bold leading-[1.1] text-center">RGPD<br />OK</div>
            </div>
            <div className="mt-6 text-[26px] font-semibold leading-tight">Vos données sont protégées.</div>
            <div className="mt-4 text-[13px] text-neutral-600 leading-relaxed">
              Bénéficiaire : <strong className="text-[#1a1d1a]">{name || 'Prévisualisation'}</strong><br />
              Émis le : <strong className="text-[#1a1d1a]">{new Date().toLocaleDateString('fr-FR')}</strong>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-[11.5px]">
              {[
                ['Chiffrement', 'AES‑256'],
                ['Auth', 'OTP + JWT'],
                ['Hébergement', 'CI + UE'],
                ['DPO', 'Joignable 24/7'],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-neutral-500 uppercase tracking-wider text-[10px]">{k}</div>
                  <div className="font-semibold text-[#1a1d1a]">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
